import * as yup from "yup";
import {useLocation, useNavigation, useSubmit} from "react-router-dom";
import {Formik} from "formik";
import BasicPrimaryButton from "../../../components/Button/BasicPrimaryButton";
import BasicErrorButton from "../../../components/Button/BasicErrorButton";
import FormikTextInput from "../../../components/Input/FormikTextInput";
import FormikDatePicker from "../../../components/Input/FormikDatePicker";
import FormikSelect from "../../../components/Input/FormikSelect";

const productCategorySchema = yup.object({
    id: yup.number().positive().integer(),
    name: yup.string(),
});
const productSchema = yup.object({
    id: yup.number().positive().integer(),
    name: yup.string(),
    defaultUnit: yup.string(),
    category: productCategorySchema,
    retired: yup.boolean(),
});
const proactiveTreatmentSchema = yup.object({
    id: yup.number().positive().integer(),
    program: yup.string().required(),
    farmUid: yup.string().required(),
    cowNumber: yup.string().trim()
        .required("Cow Number is required"),
    treatmentDate: yup.number().positive().integer()
        .required("Treatment Date is required"),
    product: productSchema
        .required("Product is required"),
    dosage: yup.number().positive().integer()
        .required("Dosage is required"),
    unit: yup.string().trim()
        .required("Unit is required"),
    comments: yup.string().trim()
        .max(511, "Must be 511 characters or less"),
});

export function formDataToProactiveTreatment(formData) {
    const proactiveTreatment = Object.fromEntries(formData);
    proactiveTreatment.product = JSON.parse(proactiveTreatment.product);

    return proactiveTreatmentSchema.cast(proactiveTreatment);
}

const ProactiveTreatmentUpdateForm = ({farm, proactiveTreatment, products, ...props}) => {
    const navigation = useNavigation();
    const submit = useSubmit();
    const location = useLocation();

    const handleDelete = event => {
        if (!window.confirm("Please confirm you want to delete")) event.preventDefault();
        else submit(null, {action: `${location.pathname}/delete`, method: "post"});
    }

    return <Formik
        initialValues={{
            program: "Herd Health Plan",
            farmUid: farm.uid,
            cowNumber: proactiveTreatment.cowNumber,
            treatmentDate: proactiveTreatment.treatmentDate,
            product: {
                ...proactiveTreatment.product,
                defaultUnit: proactiveTreatment.unit || proactiveTreatment.product.defaultUnit
            },
            dosage: proactiveTreatment.dosage,
            unit: proactiveTreatment.unit,
            cowStageId: proactiveTreatment.cowStageId,
            comments: proactiveTreatment.comments,
        }}
        validationSchema={proactiveTreatmentSchema}
        onSubmit={async values => submit({...values, product: JSON.stringify(values.product)}, {method: "post"})}
    >
        {formik => <form className="px-4 sm:px-6" method="post" onSubmit={formik.handleSubmit} {...props}>
            <div className="grid grid-cols-12 gap-y-6 gap-x-2 sm:gap-6">
                <div className="col-span-12 sm:col-span-6">
                    <FormikDatePicker label="Treatment Date" name="treatmentDate"/>
                </div>

                <div className="col-span-12 sm:col-span-6">
                    <FormikTextInput label="Cow Number" name="cowNumber" type="text"/>
                </div>

                <div className="col-span-12 sm:col-span-6">
                    <FormikSelect
                        name="product" label="Product" options={products} optionValue="id" optionLabel="name"
                        groupKey="category.name" isClearable={false}
                    />
                </div>

                <div className="col-span-6 sm:col-span-3">
                    <FormikTextInput label="Dosage" name="dosage" type="number" min={1}/>
                </div>

                <div className="col-span-6 sm:col-span-3">
                    <FormikTextInput label="Unit" name="unit" type="text"/>
                </div>

                <div className="col-span-12">
                    <FormikTextInput label="Comments" name="comments" as="textarea"/>
                </div>
            </div>

            <div className="mt-4 py-3 flex justify-between">
                <BasicPrimaryButton disabled={navigation.state === "submitting"} type="submit">
                    Submit
                </BasicPrimaryButton>

                <BasicErrorButton onClick={handleDelete} disabled={navigation.state === "submitting"}>
                    Delete
                </BasicErrorButton>
            </div>
        </form>}
    </Formik>;
}

export default ProactiveTreatmentUpdateForm;